import Axios from "axios";
import { Configuration } from "@/configuration";
import { PartialThemeConfig } from "@/config/theme/themeTypes";

/**
 * Carga remota del theme.
 *
 * GET ${Configuration.api.base}/theme con timeout corto. La respuesta
 * esperada es PartialThemeConfig — el merge campo a campo + validación
 * lo hace mergeThemeConfig en themeService.applyRemote.
 *
 * Retorna null cuando NO hay un theme usable: error de red, timeout, status
 * 4xx/5xx, o un 200 cuyo body no trae colors/brand (ver isUsableThemePayload).
 * Ante null, main.ts (Fase 49) muestra la vista de inconvenientes + auto-retry
 * en vez de montar con el default RedCapital.
 *
 * Endpoint sin auth — el theme se carga antes del login.
 */
const TIMEOUT_MS = 3000;

/**
 * Un payload de /theme es usable solo si trae al menos la seccion `colors` o
 * `brand` como objeto con contenido.
 *
 * Cubre el caso de un backend que NO señala "tenant no encontrado" con un
 * status HTTP de error, sino con un 200 + objeto (ej. `{}` o
 * `{ error: "tenant no encontrado" }`). Sin esta validacion ese objeto se
 * daria por valido, el merge no encontraria campos utiles y la app montaria
 * con el default RedCapital — justo lo que la vista de inconvenientes
 * (Fase 49) busca evitar.
 */
function isUsableThemePayload(data: any): boolean {
  if (!data || typeof data !== "object") return false;
  const hasColors =
    data.colors && typeof data.colors === "object" && Object.keys(data.colors).length > 0;
  const hasBrand =
    data.brand && typeof data.brand === "object" && Object.keys(data.brand).length > 0;
  return Boolean(hasColors || hasBrand);
}

export async function loadThemeConfig(): Promise<PartialThemeConfig | null> {
  try {
    const { data } = await Axios.get(
      `${Configuration.api.base}/theme`,
      { timeout: TIMEOUT_MS }
    );
    if (isUsableThemePayload(data)) {
      return data as PartialThemeConfig;
    }
    return null;
  } catch (e) {
    return null;
  }
}
